import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { GROUP_CONTEXT, PROPERTIES } from '../data';
import type {
  GroupContext,
  Property,
  ScopeKind,
  ScopeSelection,
  Tag,
  View,
} from '../types';
import { OrganizationService } from './organization.service';
import { TagService } from './tag.service';
import { UserPreferencesService } from './user-preferences.service';
import { ViewService } from './view.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ScopeService {
  private readonly prefs = inject(UserPreferencesService);
  private readonly tagService = inject(TagService);
  private readonly viewService = inject(ViewService);
  private readonly organizations = inject(OrganizationService);
  private readonly router = inject(Router);

  readonly accounts: ReadonlyArray<Property> = PROPERTIES;

  get group(): GroupContext {
    const active = this.organizations.active();
    if (!active) return GROUP_CONTEXT;
    return { id: active.id, name: active.name, initials: active.initials };
  }

  private readonly state = this.prefs.scope;

  private readonly sessionExcludedIds = signal<ReadonlyArray<string>>([]);
  private readonly sessionViewTagIds = signal<ReadonlyArray<string> | null>(null);

  readonly selection = computed<ScopeSelection>(() => this.state().selection);
  readonly scopeKind = computed<ScopeKind>(() => this.selection().kind);

  constructor() {
    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationStart) this.clearSessionOverrides();
    });
  }

  readonly selectedView = computed<View | null>(() => {
    const sel = this.selection();
    return sel.kind === 'view' ? this.viewService.viewById(sel.id) : null;
  });

  readonly selectedTag = computed<Tag | null>(() => {
    const sel = this.selection();
    return sel.kind === 'tag' ? this.tagService.tagById(sel.id) : null;
  });

  readonly selectedAccount = computed<Property | null>(() => {
    const sel = this.selection();
    if (sel.kind !== 'account') return null;
    return this.accounts.find((account) => account.id === sel.id) ?? null;
  });

  readonly isAllScope = computed<boolean>(() => this.selection().kind === 'all');

  readonly scopeIcon = computed<string>(() => {
    switch (this.selection().kind) {
      case 'view':
        return 'dashboard_customize';
      case 'tag':
        return 'sell';
      case 'account':
        return 'apartment';
      default:
        return 'public';
    }
  });

  readonly scopeLabel = computed<string>(() => {
    const sel = this.selection();
    switch (sel.kind) {
      case 'view':
        return this.selectedView()?.name ?? 'All Accounts';
      case 'tag':
        return this.selectedTag()?.name ?? 'All Accounts';
      case 'account':
        return this.selectedAccount()?.name ?? 'All Accounts';
      default:
        return 'All Accounts';
    }
  });

  readonly scopeAccounts = computed<ReadonlyArray<Property>>(() => {
    const sel = this.selection();
    if (sel.kind === 'all') return this.accounts;
    if (sel.kind === 'account') {
      const account = this.selectedAccount();
      return account ? [account] : [];
    }
    const ids = this.resolvedIds(sel);
    const allowed = new Set(ids);
    return this.accounts.filter((account) => allowed.has(account.id));
  });

  readonly excludedAccountIds = computed<ReadonlyArray<string>>(() => this.sessionExcludedIds());

  readonly activeAccounts = computed<ReadonlyArray<Property>>(() => {
    const excluded = new Set(this.excludedAccountIds());
    if (excluded.size === 0) return this.scopeAccounts();
    return this.scopeAccounts().filter((account) => !excluded.has(account.id));
  });

  readonly activeAccountCount = computed<number>(() => this.activeAccounts().length);

  readonly viewTags = computed<ReadonlyArray<Tag>>(() => {
    const view = this.selectedView();
    if (!view) return [];
    return view.tagIds
      .map((id) => this.tagService.tagById(id))
      .filter((tag): tag is Tag => tag != null)
      .filter((tag) => this.tagService.category(tag.categoryId)?.typeKey !== view.typeKey);
  });

  readonly viewTagIds = computed<ReadonlyArray<string>>(() => {
    const override = this.sessionViewTagIds();
    if (override) return override;
    return this.selectedView()?.tagIds ?? [];
  });

  readonly favoriteViewIds = computed<ReadonlyArray<string>>(() => this.state().favoriteViewIds);
  readonly favoriteViews = computed<ReadonlyArray<View>>(() =>
    this.favoriteViewIds()
      .map((id) => this.viewService.viewById(id))
      .filter((view): view is View => view != null && this.viewService.isVisible(view)),
  );

  readonly favoriteTagIds = computed<ReadonlyArray<string>>(() => this.state().favoriteTagIds);
  readonly favoriteTags = computed<ReadonlyArray<Tag>>(() =>
    this.favoriteTagIds()
      .map((id) => this.tagService.tagById(id))
      .filter((tag): tag is Tag => tag != null && this.tagService.isVisible(tag)),
  );

  readonly favoriteAccountIds = computed<ReadonlyArray<string>>(
    () => this.state().favoriteAccountIds,
  );
  readonly favoriteAccounts = computed<ReadonlyArray<Property>>(() =>
    this.favoriteAccountIds()
      .map((id) => this.accounts.find((account) => account.id === id))
      .filter((account): account is Property => account != null),
  );

  selectAll(): void {
    this.setSelection({ kind: 'all' });
  }

  selectView(id: string): void {
    this.setSelection({ kind: 'view', id });
  }

  selectTag(id: string): void {
    this.setSelection({ kind: 'tag', id });
  }

  selectAccount(id: string): void {
    this.setSelection({ kind: 'account', id });
  }

  isFavoriteView(id: string): boolean {
    return this.favoriteViewIds().includes(id);
  }

  toggleFavoriteView(id: string): void {
    this.prefs.patch('scope', { favoriteViewIds: this.toggle(this.favoriteViewIds(), id) });
  }

  isFavoriteTag(id: string): boolean {
    return this.favoriteTagIds().includes(id);
  }

  toggleFavoriteTag(id: string): void {
    this.prefs.patch('scope', { favoriteTagIds: this.toggle(this.favoriteTagIds(), id) });
  }

  isFavoriteAccount(id: string): boolean {
    return this.favoriteAccountIds().includes(id);
  }

  toggleFavoriteAccount(id: string): void {
    this.prefs.patch('scope', { favoriteAccountIds: this.toggle(this.favoriteAccountIds(), id) });
  }

  isViewTagActive(tagId: string): boolean {
    return this.viewTagIds().includes(tagId);
  }

  toggleViewTag(tagId: string): void {
    const view = this.selectedView();
    if (!view) return;
    const current = this.sessionViewTagIds() ?? view.tagIds;
    this.sessionViewTagIds.set(this.toggle(current, tagId));
  }

  isAccountExcluded(accountId: string): boolean {
    return this.sessionExcludedIds().includes(accountId);
  }

  toggleAccountExclusion(accountId: string): void {
    this.sessionExcludedIds.set(this.toggle(this.sessionExcludedIds(), accountId));
  }

  clearExclusions(): void {
    if (this.sessionExcludedIds().length === 0) return;
    this.sessionExcludedIds.set([]);
  }

  excludeAllInScope(): void {
    this.sessionExcludedIds.set(this.scopeAccounts().map((account) => account.id));
  }

  private resolvedIds(sel: ScopeSelection): ReadonlyArray<string> {
    if (sel.kind === 'tag') return this.tagService.objectIdsForTag(sel.id, 'account');
    if (sel.kind === 'view') {
      const view = this.viewService.viewById(sel.id);
      if (!view) return [];
      return this.viewService.previewObjectIds(this.viewTagIds(), view.matchMode, 'account');
    }
    return [];
  }

  private setSelection(selection: ScopeSelection): void {
    this.clearSessionOverrides();
    this.prefs.patch('scope', { selection });
  }

  private clearSessionOverrides(): void {
    this.sessionExcludedIds.set([]);
    this.sessionViewTagIds.set(null);
  }

  private toggle(current: ReadonlyArray<string>, id: string): ReadonlyArray<string> {
    return current.includes(id) ? current.filter((value) => value !== id) : [...current, id];
  }
}
